import {Plain} from '../../../../../../shared/model/plain.type.model';
import {LabelTranslator} from '../../../model/label-translator.model';
import {EMAIL_BLOCK_TYPE} from './email-block-type.model';
import {buildEmailColumn, EmailColumn} from './email-column.model';
import {EmailBlock} from './email-block.model';
import {EMAIL_DEFAULT_BG_COLOR} from './email-default-bg-color.model';
import {EmailRowOptions} from './email-row-options.model';
import {EMAIL_ROW_TYPE} from './email-row-type.model';
import {toEmailColumn} from './to-email-column.model';

/**
 * Répartition de la largeur selon le nombre de colonnes. Les tiers ne tombent pas ronds : la
 * valeur reprise ici est celle que le back rend, pour que l'éditeur et l'e-mail envoyé s'accordent.
 */
const ROW_COLUMN_WIDTHS: Record<EMAIL_ROW_TYPE, number[]> = {
    [EMAIL_ROW_TYPE.SINGLE]: [100],
    [EMAIL_ROW_TYPE.DOUBLE]: [50, 50],
    [EMAIL_ROW_TYPE.TRIPLE]: [33.3333, 33.3333, 33.3333],
};

/** Une section de l'e-mail : une bande sur toute la largeur, découpée en une à trois colonnes. */
export class EmailRow {
    public type: EMAIL_ROW_TYPE;
    public columns: EmailColumn[];
    public options: EmailRowOptions;

    /**
     * Le gabarit du serveur marque ainsi la section des mentions légales : elle se supprime comme
     * les autres, mais pas sans être prévenu.
     */
    public warnOnDelete?: boolean;

    constructor(data: Plain<EmailRow>) {
        this.type = data.type ?? EMAIL_ROW_TYPE.SINGLE;
        this.columns = (data.columns ?? []).map(toEmailColumn);
        this.options = data.options ?? {
            grouped: false,
            bgColor: EMAIL_DEFAULT_BG_COLOR,
            padding: 0,
            border: {thickness: 0, style: 'solid', color: EMAIL_DEFAULT_BG_COLOR},
        };
        this.warnOnDelete = data.warnOnDelete;
    }

    /**
     * La section qui porte le lien de désabonnement.
     *
     * Elle est imposée par la loi : elle ne se sélectionne pas, ne se déplace pas et ne se supprime
     * pas. On la reconnaît à son contenu, le serveur ne la marquant pas autrement.
     */
    public readonly isUnsubscribe = (): boolean =>
        this.columns[0]?.content[0]?.type === EMAIL_BLOCK_TYPE.UNSUBSCRIBE_LINK;

    public readonly columnWidths = (): number[] => this.columns.map((column) => column.options.width);

    /**
     * Copie indépendante de la section, colonnes et blocs refaits un à un.
     *
     * `toEmailColumn` laisse passer une colonne qui en est déjà une (cf. son commentaire) : une
     * section reconstruite à partir d'elle-même rendrait donc les objets mêmes de l'originale. Or
     * l'éditeur suit ce qu'il affiche par l'identité des objets — deux sections qui partageraient
     * leurs blocs n'en feraient qu'une à ses yeux, et l'une porterait à l'écran ce qu'on écrit dans
     * l'autre.
     */
    public readonly copy = (): EmailRow =>
        new EmailRow({
            ...this,
            columns: this.columns.map(
                (column) =>
                    new EmailColumn({...column, content: column.content.map((block): EmailBlock => ({...block}))})
            ),
        });

    /**
     * La section, ses libellés de gabarit traduits. Rendue à l'identique quand aucune de ses
     * colonnes n'en portait.
     */
    public readonly withTranslatedTexts = (translate: LabelTranslator): EmailRow => {
        const columns = this.columns.map((column) => column.withTranslatedTexts(translate));
        if (columns.every((column, rank) => column === this.columns[rank])) return this;

        return new EmailRow({...this, columns});
    };

    public readonly withOptions = (patch: Partial<EmailRowOptions>): EmailRow =>
        new EmailRow({...this, options: {...this.options, ...patch}});

    public readonly withColumn = (index: number, column: EmailColumn): EmailRow =>
        new EmailRow({...this, columns: this.columns.map((current, rank) => (rank === index ? column : current))});

    /**
     * Retaille les colonnes. Les largeurs viennent du curseur du panneau, qui les tient à 100 au
     * total ; il en faut une par colonne, sinon la section reste telle quelle.
     */
    public readonly withColumnWidths = (widths: number[]): EmailRow => {
        if (widths.length !== this.columns.length) return this;

        return new EmailRow({
            ...this,
            columns: this.columns.map((column, rank) => column.withOptions({width: widths[rank]})),
        });
    };

    /**
     * Change le nombre de colonnes de la section.
     *
     * Les largeurs repartent de la répartition égale du nouveau découpage : celles de l'ancien
     * n'auraient plus le compte. Le contenu des colonnes en trop rejoint la dernière colonne
     * conservée — réduire le découpage ne doit pas faire disparaître des blocs sans rien annoncer.
     */
    public readonly withType = (type: EMAIL_ROW_TYPE): EmailRow => {
        const widths = ROW_COLUMN_WIDTHS[type];
        const overflow = this.columns.slice(widths.length).flatMap((column) => column.content);

        const columns = widths.map((width, rank) => {
            const kept = this.columns[rank] ?? buildEmailColumn(width, this.options.bgColor);
            const content = rank === widths.length - 1 ? [...kept.content, ...overflow] : kept.content;

            return new EmailColumn({...kept, content, options: {...kept.options, width}});
        });

        return new EmailRow({...this, type, columns});
    };
}

/** Section vierge au découpage demandé, telle que la palette la dépose dans le canevas. */
export const buildEmailRow = (type: EMAIL_ROW_TYPE, bgColor = EMAIL_DEFAULT_BG_COLOR): EmailRow =>
    new EmailRow({
        type,
        columns: ROW_COLUMN_WIDTHS[type].map((width) => buildEmailColumn(width, bgColor)),
        options: {
            grouped: false,
            bgColor,
            padding: 0,
            border: {thickness: 0, style: 'solid', color: bgColor},
        },
    });
